import {useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {Textarea} from '@/components/ui/textarea'
import {HelpIcon} from '@/components/help-icon'
import {PageHeading} from '@/components/page-heading'
import {SectionCard} from '@/components/section-card'
import {Campo, ChipOpcion} from '@/components/campo'
import {ReadOnlyNotice} from '@/components/read-only-notice'
import {EmployeeSingleSelect} from '@/components/employee-single-select'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {CertificadoPicker} from '@/components/incapacidades/certificado-picker'
import {useAllEmployees, useMyEmployee, type Employee} from '@/hooks/empresa/identidad/use-employees'
import {useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import {EVENTOS_SELECTOR_PAGE_SIZE, useEmpleadoEventos} from '@/hooks/sgsst/incidentes/use-incidentes'
import {
  mintIncapacidadUploadTarget,
  useReportarIncapacidad,
  useSubirCertificadoIncapacidad,
  type IncapacidadOrigen,
} from '@/hooks/empresa/mi_espacio/use-incapacidades'
import {uploadToStorage} from '@/hooks/sgsst/evidencias/use-evidence'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {translateApiError} from '@/lib/errors'
import {formatShortDate, localToday} from '@/lib/risk'
import {tope} from '@/lib/campos-texto'

const ORIGENES: IncapacidadOrigen[] = ['laboral', 'comun']

/** Cualquier miembro se reporta a sí mismo; un operador puede reportar por otra persona. */
export function IncapacidadReportarPage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const navigate = useNavigate()
  const {data: me} = useMyEmployee(businessId)
  const {data: roster} = useAllEmployees(businessId)
  const capabilities = useCapabilities(businessId)
  const reportar = useReportarIncapacidad(businessId!)
  const subirCertificado = useSubirCertificadoIncapacidad(businessId!)
  const godReadOnly = useGodModeReadOnly()

  const [empleado, setEmpleado] = useState<Employee | null>(null)
  const [origen, setOrigen] = useState<IncapacidadOrigen>('comun')
  const [incidenteId, setIncidenteId] = useState<string | null>(null)
  // nosemgrep: browser-clock-business-date -- date input default value, legitimate data-entry (not a compliance decision)
  const [fechaInicio, setFechaInicio] = useState(localToday())
  const [aunAbierta, setAunAbierta] = useState(true)
  const [fechaFin, setFechaFin] = useState('')
  const [entidad, setEntidad] = useState('')
  const [notas, setNotas] = useState('')
  const [certProgress, setCertProgress] = useState<number | null>(null)
  const [certFile, setCertFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)

  const yaSoyElEmpleado = !!me?.id && empleado?.id === me.id
  const empleadoActivo = empleado ?? me ?? null
  const {data: eventosPagina} = useEmpleadoEventos(
    origen === 'laboral' && capabilities.sgsst_operar ? businessId : undefined,
    origen === 'laboral' && capabilities.sgsst_operar ? empleadoActivo?.id : undefined,
    1,
    EVENTOS_SELECTOR_PAGE_SIZE
  )
  // Opciones de un combobox buscable, no una lista de entidades: no lleva pie de paginación.
  const eventos = eventosPagina?.items

  async function subirCertificadoSiHay(incapacidadId: string, file: File) {
    setCertProgress(0)
    const target = await mintIncapacidadUploadTarget(businessId!, incapacidadId, file)
    await uploadToStorage(target, file, setCertProgress)
    await subirCertificado.mutateAsync({incapacidadId, objectKey: target.object_key, file})
    setCertProgress(null)
  }

  async function submit() {
    setError(null)
    try {
      const incapacidad = await reportar.mutateAsync({
        employee_id: empleado?.id ?? null,
        origen,
        incidente_id: origen === 'laboral' ? incidenteId : null,
        fecha_inicio: fechaInicio,
        fecha_fin: aunAbierta ? null : fechaFin || null,
        entidad: entidad.trim() || null,
        notas: notas.trim() || null,
      })
      if (certFile) await subirCertificadoSiHay(incapacidad.id, certFile)
      navigate(
        empleado && !yaSoyElEmpleado
          ? `/businesses/${businessId}/empleados/${incapacidad.employee_id}?tab=incapacidades`
          : `/businesses/${businessId}/mi/informacion?tab=incapacidades`
      )
    } catch (err) {
      setCertProgress(null)
      setError(translateApiError(err))
    }
  }

  const pending = reportar.isPending || certProgress !== null
  const valido = fechaInicio.length > 0 && (aunAbierta || fechaFin.length > 0)
  const faltan = [
    fechaInicio.length > 0 ? null : t('incapacidades.reportar.faltaInicio'),
    aunAbierta || fechaFin.length > 0 ? null : t('incapacidades.reportar.faltaFin'),
  ].filter((x): x is string => x !== null)

  return (
    <div className="space-y-5" data-testid="incapacidad-reportar">
      <PageHeading title={t('incapacidades.reportar.title')} />

      <div className="mx-auto max-w-lg space-y-5">
        {godReadOnly && (
          <ReadOnlyNotice testId="incapacidad-reportar-readonly-banner">
            {t('incapacidades.reportar.readOnlyBanner')}
          </ReadOnlyNotice>
        )}

        <SectionCard title={t('incapacidades.reportar.bloqueMinimo')} bodyClassName="space-y-5 p-5">
          <Campo
            label={t('incapacidades.reportar.quienLabel')}
            accion={
              <HelpIcon label={t('incapacidades.reportar.quienTipLabel')} testId="incapacidad-quien-tip">
                {t('incapacidades.reportar.quienTip')}
              </HelpIcon>
            }>
            {me?.id && !yaSoyElEmpleado && (
              <Button
                type="button"
                variant="secondary"
                data-testid="incapacidad-quien-yo"
                onClick={() => setEmpleado(me)}
                className="min-h-11 w-full sm:w-auto">
                {t('incapacidades.reportar.quienYo')}
              </Button>
            )}
            <EmployeeSingleSelect
              roster={roster ?? []}
              value={empleado}
              onChange={setEmpleado}
              testId="incapacidad-quien-empleado"
            />
          </Campo>

          <Campo
            label={t('incapacidades.reportar.origenLabel')}
            hint={t(`incapacidades.reportar.origenHelp.${origen}`)}>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label={t('incapacidades.reportar.origenLabel')}>
              {ORIGENES.map((value) => (
                <ChipOpcion
                  key={value}
                  data-testid={`incapacidad-origen-${value}`}
                  seleccionado={origen === value}
                  onClick={() => {
                    setOrigen(value)
                    if (value === 'comun') setIncidenteId(null)
                  }}>
                  {t(`incapacidades.origen.${value}`)}
                </ChipOpcion>
              ))}
            </div>
          </Campo>

          {/* Siempre opcional (core/incapacidad.py::valida_origen_incidente); el selector solo lo ve un
            operador porque `GET …/employees/{id}/eventos` es de operador. */}
          {origen === 'laboral' && (
            <Campo
              label={t('incapacidades.reportar.incidenteLabel')}
              hint={t('incapacidades.reportar.incidenteHint')}
              opcional>
              {capabilities.sgsst_operar &&
                (!eventos || eventos.length === 0 ? (
                  <p className="text-xs text-muted-foreground">{t('incapacidades.reportar.incidenteEmpty')}</p>
                ) : (
                  <EntitySearchSelect
                    items={eventos.filter((ev) => ev.tipo === 'accidente')}
                    value={eventos.find((ev) => ev.id === incidenteId) ?? null}
                    getKey={(ev) => ev.id}
                    filterText={(ev) => `${ev.evento_fecha} ${t(`incidentes.tipo.${ev.tipo}`)}`}
                    renderOption={(ev) => (
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <span className="font-mono">{formatShortDate(ev.evento_fecha)}</span>
                        <span>{t(`incidentes.tipo.${ev.tipo}`)}</span>
                        <Tag tone="neutral">{t(`empleadoEventos.rol.${ev.rol}`)}</Tag>
                      </span>
                    )}
                    renderTriggerValue={(ev) => (
                      <span>
                        {formatShortDate(ev.evento_fecha)} · {t(`incidentes.tipo.${ev.tipo}`)}
                      </span>
                    )}
                    placeholder={t('incapacidades.reportar.incidenteNone')}
                    searchPlaceholder={t('incapacidades.reportar.incidenteSearchPlaceholder')}
                    noResultsLabel={t('incapacidades.reportar.incidenteNoResults')}
                    clearLabel={t('incapacidades.reportar.incidenteNone')}
                    testId="incapacidad-incidente-select"
                    onPick={(ev) => setIncidenteId(ev?.id ?? null)}
                  />
                ))}
            </Campo>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Campo label={t('incapacidades.reportar.fechaInicioLabel')} htmlFor="incapacidad-fecha-inicio">
              <Input
                id="incapacidad-fecha-inicio"
                type="date"
                value={fechaInicio}
                data-testid="incapacidad-fecha-inicio"
                // nosemgrep: browser-clock-business-date -- date input max, legitimate data-entry (not a compliance decision)
                max={localToday()}
                onChange={(e) => setFechaInicio(e.target.value)}
                className="h-11 font-mono tabular-nums sm:h-9"
              />
            </Campo>
            <Campo label={t('incapacidades.reportar.fechaFinLabel')} htmlFor="incapacidad-fecha-fin">
              <Input
                id="incapacidad-fecha-fin"
                type="date"
                value={fechaFin}
                disabled={aunAbierta}
                data-testid="incapacidad-fecha-fin"
                min={fechaInicio}
                onChange={(e) => setFechaFin(e.target.value)}
                className="h-11 font-mono tabular-nums sm:h-9"
              />
            </Campo>
          </div>
          <label className="flex min-h-11 items-center gap-2 text-sm text-foreground sm:min-h-0">
            <input
              type="checkbox"
              checked={aunAbierta}
              data-testid="incapacidad-aun-abierta"
              onChange={(e) => {
                setAunAbierta(e.target.checked)
                if (e.target.checked) setFechaFin('')
              }}
              className="size-4 shrink-0 accent-primary"
            />
            {t('incapacidades.reportar.aunAbierta')}
          </label>
        </SectionCard>

        <SectionCard title={t('incapacidades.reportar.bloqueSoporte')} bodyClassName="space-y-5 p-5">
          <Campo label={t('incapacidades.reportar.entidadLabel')} htmlFor="incapacidad-entidad" opcional>
            <Input
              id="incapacidad-entidad"
              type="text"
              value={entidad}
              maxLength={tope('RAZ')}
              data-testid="incapacidad-entidad"
              onChange={(e) => setEntidad(e.target.value)}
              placeholder={t('incapacidades.reportar.entidadPlaceholder')}
              className="h-11 sm:h-9"
            />
          </Campo>

          <Campo label={t('incapacidades.reportar.notasLabel')} htmlFor="incapacidad-notas" opcional>
            <Textarea
              id="incapacidad-notas"
              value={notas}
              maxLength={2000}
              data-testid="incapacidad-notas"
              onChange={(e) => setNotas(e.target.value)}
              placeholder={t('incapacidades.reportar.notasPlaceholder')}
              minRows={3}
            />
          </Campo>

          <Campo
            label={t('incapacidades.reportar.certificadoLabel')}
            hint={t('incapacidades.reportar.certificadoHint')}
            opcional>
            <CertificadoPicker
              file={certFile}
              progress={certProgress}
              onPick={setCertFile}
              onError={setError}
              testIdPrefix="incapacidad-certificado"
              className="min-h-11 sm:min-h-0"
            />
          </Campo>
        </SectionCard>

        {error && (
          <p className="text-sm text-destructive" data-testid="incapacidad-reportar-error">
            {error}
          </p>
        )}

        <div className="sticky bottom-0 -mx-4 border-t border-border bg-background/95 px-4 py-3 backdrop-blur-sm md:static md:mx-0 md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
          <Button
            className="w-full"
            size="lg"
            data-testid="incapacidad-reportar-submit"
            disabled={!valido || pending || godReadOnly}
            onClick={submit}>
            {t('incapacidades.reportar.submit')}
          </Button>
          {faltan.length > 0 && (
            <p className="mt-2 text-center text-xs text-muted-foreground" data-testid="incapacidad-reportar-falta">
              {t('common.falta', {campos: faltan.join(' · ')})}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
